'use client';

import { useState, useEffect, useCallback } from 'react';
import { apiConfig } from '@/lib/api-config';
import { useAuth } from '@/contexts/AuthContext';
import { authFetch } from '@/lib/auth-fetch';
import type { Ticket, TicketDetail } from '../types';

interface UseTicketsDataProps {
  onSessionLaunched?: (sessionId: number) => void;
}

export function useTicketsData({ onSessionLaunched }: UseTicketsDataProps = {}) {
  const { token } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<number | null>(null);
  const [ticketDetail, setTicketDetail] = useState<TicketDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchTickets = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!token) {
      setTickets([]);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      const url = apiConfig.endpoints.tickets.list({ limit: 100 });
      const response = await authFetch(url);
      
      if (!response.ok) {
        // Handle 401 gracefully - user not authenticated
        if (response.status === 401) {
          setTickets([]);
          setError(null);
          setLoading(false);
          return;
        }
        
        let errorMessage = `Failed to fetch tickets: ${response.status}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData?.detail || errorData?.message || errorMessage;
          } else {
            const errorText = await response.text();
            console.error('Non-JSON error response:', errorText.substring(0, 200));
            errorMessage = `Server error: ${response.status}`;
          }
        } catch (parseErr) {
          console.error('Error parsing error response:', parseErr);
        }
        throw new Error(errorMessage);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response received:', text.substring(0, 200));
        throw new Error('Server returned non-JSON response');
      }
      
      const data = await response.json();
      setTickets(data.tickets || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      // Don't set error for 401 - just show empty state
      if (err instanceof Error && err.message.includes('401')) {
        setTickets([]);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch tickets');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTicketDetail = useCallback(async (ticketId: number) => {
    // Don't fetch if not authenticated
    if (!token) {
      setTicketDetail(null);
      setLoadingDetail(false);
      return;
    }

    setLoadingDetail(true);
    setTicketDetail(null);
    try {
      const url = apiConfig.endpoints.tickets.detail(ticketId);
      const response = await authFetch(url);
      
      if (!response.ok) {
        // Handle 401 gracefully
        if (response.status === 401) {
          setTicketDetail(null);
          setLoadingDetail(false);
          return;
        }
        throw new Error(`Failed to fetch ticket detail: ${response.status}`);
      }
      const data = await response.json();
      setTicketDetail(data);
    } catch (err) {
      console.error('Error fetching ticket detail:', err);
      // Don't set error for 401
      if (!(err instanceof Error && err.message.includes('401'))) {
        setError(err instanceof Error ? err.message : 'Failed to fetch ticket detail');
      }
    } finally {
      setLoadingDetail(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTickets();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchTickets, 10000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  useEffect(() => {
    if (selectedTicket) {
      fetchTicketDetail(selectedTicket);
    } else {
      setTicketDetail(null);
    }
  }, [selectedTicket, fetchTicketDetail]);

  const filteredTickets = tickets.filter((ticket: Ticket) => {
    if (filterStatus !== 'all' && ticket.status !== filterStatus) {
      return false;
    }
    if (filterSeverity !== 'all' && ticket.severity !== filterSeverity) {
      return false;
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.title.toLowerCase().includes(query) ||
        (ticket.description && ticket.description.toLowerCase().includes(query)) ||
        ticket.source.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const inferConnectorType = useCallback((ticket: Ticket | TicketDetail | null) => {
    if (!ticket) return 'ssh';
    const env = (ticket.environment || '').toLowerCase();
    if (env.includes('win')) return 'winrm';
    if (env.includes('k8') || env.includes('kubernetes')) return 'kubernetes';
    return 'ssh';
  }, []);

  const extractHostFromDescription = useCallback((description?: string | null) => {
    if (!description) return undefined;
    const hostPattern = /\b[a-z0-9]+(?:-[a-z0-9]+){1,}\b/gi;
    const matches = description.match(hostPattern);
    if (!matches) return undefined;
    const prioritized = matches.find((candidate) =>
      /(prod|db|web|app|srv|srv|server)/i.test(candidate)
    );
    return (prioritized || matches[0])?.toLowerCase();
  }, []);

  const buildExecutionMetadata = useCallback((ticketId: number) => {
    const detailMatch =
      ticketDetail && ticketDetail.id === ticketId ? ticketDetail : null;
    const listMatch =
      tickets.find((item: Ticket) => item.id === ticketId) ?? (detailMatch || null);
    const hostFromMeta =
      detailMatch?.meta_data?.configuration_item ||
      detailMatch?.meta_data?.hostname ||
      detailMatch?.meta_data?.server ||
      detailMatch?.meta_data?.ci;
    const fallbackHost =
      extractHostFromDescription(detailMatch?.description) ||
      extractHostFromDescription(listMatch?.description || undefined);

    const metadata: Record<string, any> = {
      ticket_title: listMatch?.title,
      severity: listMatch?.severity,
      environment: listMatch?.environment,
      service: listMatch?.service,
      source: listMatch?.source,
      connector_type: inferConnectorType(listMatch),
      credential_source: detailMatch?.meta_data?.credential_source || 'vault',
      target: undefined,
      ticket_reference: {
        id: ticketId,
      },
    };

    const hostCandidate = hostFromMeta || fallbackHost;
    if (hostCandidate) {
      metadata.target = {
        host: hostCandidate,
        environment: listMatch?.environment,
        service: listMatch?.service,
      };
    }

    if (detailMatch?.meta_data) {
      metadata.ticket_meta = detailMatch.meta_data;
    }

    if (!metadata.target) {
      delete metadata.target;
    }

    if (!metadata.service) {
      delete metadata.service;
    }

    return metadata;
  }, [tickets, ticketDetail, inferConnectorType, extractHostFromDescription]);

  const handleExecuteRunbook = useCallback(async (
    ticketId: number,
    runbookId: number
  ) => {
    console.log(`[Execute] Starting execution for ticket ${ticketId}, runbook ${runbookId}`);
    try {
      const metadata = buildExecutionMetadata(ticketId);
      const detailMatch =
        ticketDetail && ticketDetail.id === ticketId ? ticketDetail : null;
      const listMatch =
        tickets.find((item: Ticket) => item.id === ticketId) ?? (detailMatch || null);

      const requestBody = {
        runbook_id: runbookId,
        ticket_id: ticketId,
        issue_description: detailMatch?.description || listMatch?.description || detailMatch?.title || listMatch?.title,
        metadata,
      };
      
      const endpoint = apiConfig.endpoints.executions.createSession();
      console.log(`[Execute] Calling ${endpoint}`);
      console.log(`[Execute] Request body:`, JSON.stringify(requestBody, null, 2));
      console.log(`[Execute] About to call fetch...`);

      let response;
      try {
        // Add timeout to detect hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
          console.error(`[Execute] Request timeout after 30 seconds`);
        }, 30000);

        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        console.log(`[Execute] Fetch completed, status: ${response.status}`);
        console.log(`[Execute] Response headers:`, Object.fromEntries(response.headers.entries()));
      } catch (fetchError: any) {
        if (fetchError.name === 'AbortError') {
          console.error(`[Execute] Request was aborted (timeout)`);
          throw new Error('Request timeout - server took too long to respond');
        }
        console.error(`[Execute] Fetch error:`, fetchError);
        console.error(`[Execute] Fetch error details:`, {
          name: fetchError.name,
          message: fetchError.message,
          stack: fetchError.stack,
        });
        throw new Error(`Network error: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
      }

      console.log(`[Execute] Response status: ${response.status}`);
      console.log(`[Execute] Response ok: ${response.ok}`);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { detail: `HTTP ${response.status}: ${response.statusText}` };
        }
        console.error(`[Execute] Error response:`, errorData);
        throw new Error(errorData.detail || 'Failed to execute runbook');
      }

      let data;
      try {
        const responseText = await response.text();
        console.log(`[Execute] Response text:`, responseText);
        data = JSON.parse(responseText);
      } catch (e) {
        console.error(`[Execute] Failed to parse response:`, e);
        throw new Error('Invalid response from server');
      }
      
      console.log(`[Execute] Success response:`, data);
      const sessionId = data?.id ?? data?.session_id;
      console.log(`[Execute] Extracted sessionId:`, sessionId);
      console.log(`[Execute] onSessionLaunched callback exists:`, !!onSessionLaunched);
      
      if (sessionId) {
        // Navigate to agent-workspace immediately without alert
        console.log(`[Execute] Calling onSessionLaunched with sessionId:`, sessionId);
        if (onSessionLaunched) {
          onSessionLaunched(sessionId);
          console.log(`[Execute] onSessionLaunched called successfully`);
        } else {
          console.error(`[Execute] onSessionLaunched is not defined!`);
          alert(`Session created (ID: ${sessionId}) but navigation callback not available`);
        }
        setSelectedTicket(null);
        setTicketDetail(null);
      } else {
        // Only show alert if session creation failed
        console.error(`[Execute] No sessionId in response:`, data);
        alert('Execution session created but no session ID returned');
      }
      
      // Refresh tickets and detail
      await fetchTickets();
      if (selectedTicket === ticketId) {
        await fetchTicketDetail(ticketId);
      }
    } catch (err) {
      console.error(`[Execute] Exception:`, err);
      alert(`Failed to execute runbook: ${err instanceof Error ? err.message : 'Unknown error'}`);
      throw err;
    }
  }, [tickets, ticketDetail, selectedTicket, buildExecutionMetadata, fetchTickets, fetchTicketDetail, onSessionLaunched]);

  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      case 'escalated':
        return 'bg-red-100 text-red-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'analyzing':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }, []);

  const getSeverityColor = useCallback((severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }, []);

  return {
    tickets: filteredTickets,
    loading,
    error,
    selectedTicket,
    setSelectedTicket,
    ticketDetail,
    loadingDetail,
    filterStatus,
    setFilterStatus,
    filterSeverity,
    setFilterSeverity,
    searchQuery,
    setSearchQuery,
    fetchTickets,
    fetchTicketDetail,
    handleExecuteRunbook,
    getStatusColor,
    getSeverityColor,
    filteredTickets,
  };
}

